import type { FormFieldRole, FormFieldType } from "@rosc/contracts";
import { IconButton } from "@rosc/ui/v32/button";
import { Input } from "@rosc/ui/v32/input";
import { SegmentedControl } from "@rosc/ui/v32/segmented-control";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@rosc/ui/v32/select";
import { PenLine, Sparkles, Trash2, UserRound } from "lucide-react";
import type { JSX } from "react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { FormField } from "@/lib/form-completion/queries";

const TYPES: FormFieldType[] = [
  "text",
  "multiline",
  "date",
  "checkbox",
  "radio",
  "select",
];
const ROLES: FormFieldRole[] = ["clinical", "identifier", "signature"];
const ROLE_ICON = {
  clinical: <Sparkles />,
  identifier: <UserRound />,
  signature: <PenLine />,
} as const;

const optionsText = (options: string[]) => options.join(", ");
const parseOptions = (text: string) =>
  [
    ...new Set(
      text
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ].slice(0, 30);

const FieldRow = ({
  field,
  onChange,
  onRemove,
}: {
  field: FormField;
  onChange: (field: FormField) => void;
  onRemove: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const id = useId();
  const choice = field.type === "radio" || field.type === "select";
  return (
    <li
      className="space-y-3 rounded-[14px] bg-v3-surface-2 p-3.5 shadow-v3-hairline"
      data-field-key={field.key}
      data-testid="form-field-row"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 space-y-1">
          <label
            className="flex items-center justify-between text-[12.5px] font-semibold text-v3-text-3"
            htmlFor={`${id}-label`}
          >
            {t("forms.fields.label")}
            <span className="font-normal tabular-nums">
              {t("forms.fields.page", { page: field.page })}
            </span>
          </label>
          <Input
            className="bg-v3-panel"
            id={`${id}-label`}
            maxLength={200}
            value={field.label}
            onChange={(event) =>
              onChange({ ...field, label: event.target.value })
            }
          />
        </div>
        <IconButton
          className="mt-5"
          label={t("forms.fields.remove")}
          onClick={onRemove}
        >
          <Trash2 />
        </IconButton>
      </div>
      <div className="grid gap-3">
        <div className="space-y-1">
          <span className="block text-[12.5px] font-semibold text-v3-text-3">
            {t("forms.fields.type")}
          </span>
          <Select
            value={field.type}
            onValueChange={(type) => {
              const next = type as FormFieldType;
              onChange({
                ...field,
                type: next,
                options:
                  next === "radio" || next === "select" ? field.options : [],
              });
            }}
          >
            <SelectTrigger
              aria-label={t("forms.fields.type")}
              className="bg-v3-panel"
              data-testid="form-field-type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {t(`forms.fields.types.${type}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0 space-y-1">
          <span className="block text-[12.5px] font-semibold text-v3-text-3">
            {t("forms.fields.role")}
          </span>
          <SegmentedControl
            label={t("forms.fields.role")}
            segments={ROLES.map((role) => ({
              value: role,
              label: t(`forms.fields.roles.${role}`),
              icon: ROLE_ICON[role],
              testId: `form-field-role-${role}`,
            }))}
            stretch
            value={field.role}
            onValueChange={(role) => onChange({ ...field, role })}
          />
          <p className="text-[12.5px] leading-[17px] text-v3-text-3">
            {t(`forms.fields.roleHelp.${field.role}`)}
          </p>
        </div>
      </div>
      {choice && (
        <div className="space-y-1">
          <label
            className="block text-[12.5px] font-semibold text-v3-text-3"
            htmlFor={`${id}-options`}
          >
            {t("forms.fields.options")}
          </label>
          <Input
            className="bg-v3-panel"
            id={`${id}-options`}
            placeholder={t("forms.fields.optionsHint")}
            defaultValue={optionsText(field.options)}
            onBlur={(event) =>
              onChange({ ...field, options: parseOptions(event.target.value) })
            }
          />
        </div>
      )}
    </li>
  );
};

/**
 * The second step: the clinician confirms the detected fields once (label,
 * type, who fills it). Identifiers are typed later by the clinician and
 * never sent to the model; signature fields stay blank.
 */
export const FormFieldsEditor = ({
  name,
  fields,
  onNameChange,
  onFieldsChange,
}: {
  name: string;
  fields: FormField[];
  onNameChange: (name: string) => void;
  onFieldsChange: (fields: FormField[]) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const nameId = useId();
  const counts = {
    clinical: fields.filter((field) => field.role === "clinical").length,
    identifier: fields.filter((field) => field.role === "identifier").length,
    signature: fields.filter((field) => field.role === "signature").length,
  };
  return (
    <div className="space-y-5 px-5 pb-6 pt-1" data-testid="form-fields">
      <div>
        <h2 className="font-v3-display text-[22px] font-bold leading-7 tracking-[-0.01em]">
          {t("forms.fields.heading")}
        </h2>
        <p className="mt-1.5 text-[15px] leading-[22px] text-v3-text-2">
          {t("forms.fields.description")}
        </p>
      </div>
      <div className="space-y-1">
        <label
          className="block text-[13px] font-semibold text-v3-text-2"
          htmlFor={nameId}
        >
          {t("forms.fields.name")}
        </label>
        <Input
          data-testid="form-name"
          id={nameId}
          maxLength={120}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </div>
      <p
        className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-v3-text-3"
        data-testid="form-field-counts"
      >
        <span>
          {t("forms.fields.summary.clinical", { count: counts.clinical })}
        </span>
        <span>
          {t("forms.fields.summary.identifier", { count: counts.identifier })}
        </span>
        <span>
          {t("forms.fields.summary.signature", { count: counts.signature })}
        </span>
      </p>
      <ul className="space-y-2.5">
        {fields.map((field, index) => (
          <FieldRow
            key={field.key}
            field={field}
            onChange={(next) =>
              onFieldsChange(
                fields.map((item, at) => (at === index ? next : item)),
              )
            }
            onRemove={() =>
              onFieldsChange(fields.filter((_, at) => at !== index))
            }
          />
        ))}
      </ul>
    </div>
  );
};
